import { useState, useEffect } from "react";
import * as XLSX from "xlsx";
import { apiFetch } from "../utils/apiClient";
import ExcusePopup from "./ExcusePopup";

const TIER_META = {
  1: { label: "Critical", textClass: "text-red-400",    bgClass: "bg-red-500/10",    borderClass: "border-red-500/30" },
  2: { label: "Severe",   textClass: "text-orange-400", bgClass: "bg-orange-500/10", borderClass: "border-orange-500/30" },
  3: { label: "Elevated", textClass: "text-amber-400",  bgClass: "bg-amber-500/10",  borderClass: "border-amber-500/30" },
  4: { label: "Notable",  textClass: "text-blue-400",   bgClass: "bg-blue-500/10",   borderClass: "border-blue-500/30" },
  5: { label: "Clean",    textClass: "text-white/40",   bgClass: "bg-white/[0.03]",  borderClass: "border-white/10" },
};
const TOP_N = 5;

function sortByAmount(rows) {
  return [...rows].sort((a, b) => (b.amount || 0) - (a.amount || 0));
}

// Groups rows by signal type first, so all Known-Vendor Overlap findings
// sit together, all POD findings sit together, etc. — rather than
// interleaving purely by dollar amount, which scattered related findings
// across a long list with no way to scan for just one kind. Amount still
// breaks ties within each group.
const SIGNAL_TYPE_ORDER = [
  "Exclusion",
  "Market-Wide Exclusion",
  "Known-Vendor Overlap",
  "Triple Interest",
  "Market-Wide Triple Interest",
  "POD Tier 1",
  "Payment Audit Threshold",
  "Disclosure Gap",
  "Disclosed Vendor Relationship",
  "POD Tier 2",
  "Dual Interest",
  "Market-Wide Dual Interest",
  "Vendor POD Tier 1",
  "Vendor POD Tier 2",
  "Blind-Spot",
];
function signalTypeRank(signalType) {
  const idx = SIGNAL_TYPE_ORDER.indexOf(signalType);
  return idx === -1 ? SIGNAL_TYPE_ORDER.length : idx;
}
function sortBySignalThenAmount(rows) {
  return [...rows].sort((a, b) => {
    const rankDiff = signalTypeRank(a.signalType) - signalTypeRank(b.signalType);
    if (rankDiff !== 0) return rankDiff;
    return (b.amount || 0) - (a.amount || 0);
  });
}

function rowLabel(r) {
  return r.providerName ? `${r.providerName}${r.npi ? ` · ${r.npi}` : ""}` : r.npi || r.signalType;
}

function formatMoney(n) {
  return `$${Math.round(n || 0).toLocaleString()}`;
}

// Dual/Triple Interest use their tier to distinguish the same-period /
// same-year variant from the not-concurrent one with a subtly different
// (but visually related) icon, rather than a completely unrelated symbol.
function signalIcon(r) {
  const type = r.signalType;
  if (type === "Exclusion" || type === "Market-Wide Exclusion") return "🚫";
  if (type === "Triple Interest" || type === "Market-Wide Triple Interest") return r.tier === 2 ? "🔺" : "🔻";
  if (type === "Dual Interest" || type === "Market-Wide Dual Interest") return r.tier === 3 ? "🔷" : "🔹";
  if (type === "Known-Vendor Overlap") return "🤝";
  if (type === "Blind-Spot") return "🙈";
  if (type === "POD Tier 1" || type === "POD Tier 2" || type === "Vendor POD Tier 1" || type === "Vendor POD Tier 2") return "🏭";
  if (type === "Payment Audit Threshold") return "📋";
  if (type === "Disclosure Gap") return "📝";
  if (type === "Disclosed Vendor Relationship") return "📢";
  return null;
}

const DISCLOSURE_CATEGORIES = [
  ["general", "general payments"],
  ["ownership", "ownership value"],
  ["directResearch", "direct research"],
  ["piResearch", "PI research"],
];

// Returns null for reports generated before the thresholds were recorded on
// the result, so older saved reports simply omit the line.
function describeDisclosureChecks(thresholds) {
  if (!thresholds) return null;
  const checked = [];
  const unchecked = [];
  for (const [key, label] of DISCLOSURE_CATEGORIES) {
    const t = thresholds[key];
    if (t === null || t === undefined) unchecked.push(label);
    else checked.push(t === 0 ? `${label}: any amount` : `${label} ≥ $${Number(t).toLocaleString()}`);
  }
  return { checked, unchecked };
}

function DisclosureChecksNote({ thresholds }) {
  const info = describeDisclosureChecks(thresholds);
  if (!info) return null;
  return (
    <>
      <p className="text-white/25">Checked: {info.checked.join("; ")}.</p>
      {info.unchecked.length > 0 && (
        <p className="text-amber-400/80">
          Not checked: {info.unchecked.join(", ")}. Enter a threshold (0 for any amount) in Disclosure Thresholds to include {info.unchecked.length === 1 ? "it" : "them"}.
        </p>
      )}
    </>
  );
}

const EXCUSABLE_SIGNAL_TYPES = [
  "Disclosure Gap", "Disclosed Vendor Relationship","Dual Interest", "Triple Interest", "Known-Vendor Overlap", "POD Tier 1", "Payment Audit Threshold"];
function isRowExcusable(r) {
  return r.tier <= 3 && !!r.npi && EXCUSABLE_SIGNAL_TYPES.includes(r.signalType);
}

// Clickable "Excused" badge — expands to show who excused it, the
// thresholds on file, and an Undo action. A simple inline toggle rather
// than a floating popover, to avoid positioning/z-index complexity.
function ExcusedDetail({ r, onReverse, reversing }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button
        onClick={() => setOpen(!open)}
        className="text-xs px-1.5 py-0.5 rounded-full border border-green-500/30 bg-green-500/10 text-green-400 hover:bg-green-500/20 transition-all shrink-0"
      >
        ✓ Reviewed {open ? "▲" : "▼"}
      </button>
      {open && (
        <div className="w-full mt-1 p-2 rounded-lg bg-white/[0.03] border border-white/10 text-xs text-white/50 space-y-1">
          <p>Reviewed by {r.excusedBy || "—"}{r.excusedAt ? ` · ${new Date(r.excusedAt).toLocaleDateString()}` : ""}</p>
          {r.excuseNotes && <p className="italic">"{r.excuseNotes}"</p>}
          <div className="pt-1.5 mt-1.5 border-t border-white/10">
            {r.excuseRegardlessOfAmount ? (
              <p>No review required regardless of future amount</p>
            ) : (
              <>
                <p className="text-white/35">Next payment review thresholds — apply once this relationship's data changes (a new program year, or a later correction to existing data):</p>
                <p className="mt-0.5">
                  General: {r.excuseThresholdGeneral != null ? formatMoney(r.excuseThresholdGeneral) : "any"},{" "}
                  Ownership: {r.excuseThresholdOwnership != null ? formatMoney(r.excuseThresholdOwnership) : "any"}
                  {r.signalType === "Payment Audit Threshold" ? (
                    <>
                      , Direct research: {r.excuseThresholdDirectResearch != null ? formatMoney(r.excuseThresholdDirectResearch) : "any"},{" "}
                      PI research: {r.excuseThresholdPiResearch != null ? formatMoney(r.excuseThresholdPiResearch) : "any"}
                    </>
                  ) : (
                    <>, Research: {r.excuseThresholdResearch != null ? formatMoney(r.excuseThresholdResearch) : "any"}</>
                  )}
                </p>
              </>
            )}
          </div>
          <button
            onClick={onReverse}
            disabled={reversing}
            className="text-red-400/70 hover:text-red-400 disabled:opacity-50 transition-colors"
          >
            {reversing ? "Reversing…" : "Undo review"}
          </button>
        </div>
      )}
    </>
  );
}

// Collapses a provider's repeated rows of the SAME signal type (e.g. many
// individual Blind-Spot company relationships) into one rolled-up summary
// line — distinct signal types for that provider (Dual Interest vs. POD
// Tier 2) never merge into each other. Rows with no companyId (Exclusion,
// Market-Wide Exclusion — not company-differentiated) are never collapsed.
function collapseLineItems(rows) {
  const groups = new Map();
  const standalone = [];
  rows.forEach((r) => {
    if (!r.npi || !r.companyId) { standalone.push(r); return; }
    const key = `${r.npi}::${r.signalType}`;
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key).push(r);
  });
  const collapsed = [...groups.values()].map((group) => {
    if (group.length === 1) return group[0];
    const sorted = sortByAmount(group);
    const total = group.reduce((s, r) => s + (r.amount || 0), 0);
    const topCompanies = sorted.slice(0, 3).map((r) => r.companyName).filter(Boolean);
    return {
      ...group[0],
      amount: total,
      finding: `${group.length} relationships, ${formatMoney(total)} total${topCompanies.length ? ` — top: ${topCompanies.join(", ")}` : ""}`,
    };
  });
  return [...standalone, ...collapsed];
}

// Ranks providers by their single highest-amount line item and keeps only
// the top N — but returns ALL of that provider's line items, not just one,
// so a provider's several distinct signal types still all show.
function topByProvider(items, n) {
  const byProvider = new Map();
  items.forEach((item) => {
    const key = item.npi || `${item.signalType}::${item.finding}`;
    if (!byProvider.has(key)) byProvider.set(key, []);
    byProvider.get(key).push(item);
  });
  const ranked = [...byProvider.values()]
    .map((group) => ({ group, maxAmount: Math.max(...group.map((r) => r.amount || 0)) }))
    .sort((a, b) => b.maxAmount - a.maxAmount)
    .slice(0, n);
  return { items: ranked.flatMap((g) => sortByAmount(g.group)), providerCount: ranked.length };
}

// Export-only — keeps every individual row (no collapsing), but orders them
// so a provider's rows sit together, sorted by that provider's total amount
// in this tier, rather than a global amount sort scattering one provider's
// relationships across the whole sheet.
function sortRowsForExport(rows) {
  const byProvider = new Map();
  const standalone = [];
  rows.forEach((r) => {
    if (!r.npi) { standalone.push(r); return; }
    if (!byProvider.has(r.npi)) byProvider.set(r.npi, []);
    byProvider.get(r.npi).push(r);
  });
  const blocks = [...byProvider.values()]
    .map((group) => ({ group: sortByAmount(group), total: group.reduce((s, r) => s + (r.amount || 0), 0) }))
    .sort((a, b) => b.total - a.total);
  return [...standalone, ...blocks.flatMap((b) => b.group)];
}

function applyCurrencyColumn(ws, colIndex, rowCount) {
  for (let r = 1; r <= rowCount; r++) {
    const addr = XLSX.utils.encode_cell({ r, c: colIndex });
    const cell = ws[addr];
    if (cell && typeof cell.v === "number") cell.z = "$#,##0";
  }
}

function TierGroup({ tier, rows, count, findingCount, onOpenExcuse, onReverse, reversingKey, paginationEnabled, pagination, onLoadMore }) {
  const meta = TIER_META[tier];

  // Gate on rows too, not just count — a count of 0 shouldn't hide a tier
  // that actually has real rows in it (an aggregate finding with no
  // natural per-provider count, for instance). This is a backstop, not
  // the fix itself — the real fix is giving every aggregate row an
  // accurate count at the source.
  if (count === 0 && rows.length === 0) {
    return (
      <div className="flex items-center gap-2 py-2 px-3 rounded-lg border mb-2 bg-white/[0.03] border-white/10">
        <span className="text-xs px-2 py-0.5 rounded-full border shrink-0 bg-white/10 border-white/20 text-white/40">Tier {tier}</span>
        <p className="text-white/40 text-sm">
          {tier === 5 ? `${count.toLocaleString()} provider${count !== 1 ? "s" : ""} — no signals found` : `0 ${meta.label.toLowerCase()} findings`}
        </p>
      </div>
    );
  }

  // rows already arrive collapsed and top-N'd from the server (see
  // buildResultPreview in generate-conflict-report.js) — no client-side
  // re-collapsing needed, and none of the "truncate before collapsing"
  // risk that produced the wrong Juan Uribe totals. Once a tier has been
  // expanded via Load More, rows instead comes from live pagination and
  // is already a flat, correctly-ordered list — sortByAmount is still
  // safe to apply either way.
  const shown = sortBySignalThenAmount(rows);
  const shownProviderCount = new Set(shown.filter((r) => r.npi).map((r) => r.npi)).size;
  const hiddenProviders = Math.max(0, count - shownProviderCount);
  const isExpanded = pagination?.expanded;
  const showLoadMore = paginationEnabled && tier <= 3 && (hiddenProviders > 0 || (isExpanded && pagination?.hasMore));
  return (
    <div className="py-2 border-b border-white/5">
      <div className={`flex items-center gap-2 mb-2 px-3 py-2 rounded-lg border ${meta.bgClass} ${meta.borderClass}`}>
        <span className={`text-xs px-2 py-0.5 rounded-full border shrink-0 bg-white/10 border-white/20 ${meta.textClass}`}>Tier {tier}</span>
        <p className={`text-sm font-medium ${meta.textClass}`}>
          {count.toLocaleString()} {meta.label.toLowerCase()}-tier provider{count !== 1 ? "s" : ""}
          {tier <= 4 && findingCount != null && findingCount !== count && ` · ${findingCount.toLocaleString()} finding${findingCount !== 1 ? "s" : ""}`}
        </p>
      </div>
            <div className={isExpanded ? "space-y-2 pl-1 max-h-[500px] overflow-y-auto pr-1" : "space-y-2 pl-1"}>
        {shown.map((r, i) => {
          // Genuine synthetic summary rows (Excluded Counterparty Rate,
          // Market-Wide Dual/Triple Interest) have neither npi nor
          // providerName. rowLabel() falls back to the signal type itself
          // for these, producing a redundant "X — X" header — give them
          // their own callout treatment instead of the normal row format.
          const isAggregate = !r.npi && !r.providerName;
          if (isAggregate) {
            return (
              <div key={i} className={`rounded-lg border px-3 py-2 ${TIER_META[r.tier].bgClass} ${TIER_META[r.tier].borderClass}`}>
                <p className={`text-xs font-semibold uppercase tracking-wide ${TIER_META[r.tier].textClass}`}>{signalIcon(r) ? `${signalIcon(r)} ` : ""}{r.signalType}</p>
                <p className="text-white/70 text-sm mt-0.5">{r.finding}</p>
              </div>
            );
          }
          const isExcusable = isRowExcusable(r);
          const rowExcuseKey = isExcusable ? `${r.npi}::${r.companyId}::${r.signalType}` : null;

          return (
            <div key={i} className="text-sm">
              <p className="text-white/80 flex items-center gap-1.5 flex-wrap">
                <span>
                  {signalIcon(r) && <span>{signalIcon(r)} </span>}
                  <span className={r.signalType === "Payment Audit Threshold" ? "text-gold-400 font-medium" : ""}>{r.signalType}</span>
                  {" — "}{rowLabel(r)}
                </span>
                {r.signalType === "Triple Interest" && (
                  <span className={`text-xs px-1.5 py-0.5 rounded-full border shrink-0 ${TIER_META[r.tier].bgClass} ${TIER_META[r.tier].borderClass} ${TIER_META[r.tier].textClass}`}>
                    {r.tier === 2 ? "Same year" : "Not same year"}
                  </span>
                )}
                {r.signalType === "Dual Interest" && (
                  <span className={`text-xs px-1.5 py-0.5 rounded-full border shrink-0 ${TIER_META[r.tier].bgClass} ${TIER_META[r.tier].borderClass} ${TIER_META[r.tier].textClass}`}>
                    {r.tier === 3 ? "Same period" : "Different periods"}
                  </span>
                )}
                {isExcusable && !r.excuseStatus && (
                  <button
                    onClick={() => onOpenExcuse(r)}
                    className="text-xs px-1.5 py-0.5 rounded-full border border-white/15 text-white/40 hover:text-white/70 hover:border-white/30 transition-all shrink-0"
                  >
                    ⚑ Review
                  </button>
                )}
                {isExcusable && r.excuseStatus === "holding" && (
                  <ExcusedDetail r={r} onReverse={() => onReverse(r)} reversing={reversingKey === rowExcuseKey} />
                )}
                {isExcusable && r.excuseStatus === "needs_review" && (
                  <button
                    onClick={() => onOpenExcuse(r)}
                    className="text-xs px-1.5 py-0.5 rounded-full border border-amber-500/40 bg-amber-500/10 text-amber-300 hover:bg-amber-500/20 transition-all shrink-0"
                  >
                    ⚠ Review needed
                  </button>
                )}
              </p>
              <p className="text-white/40 text-xs mt-0.5">{r.finding}</p>
              {isExcusable && r.excuseStatus === "needs_review" && (
                <p className="text-amber-400/70 text-xs mt-0.5">
                  Previously reviewed by {r.excusedBy} ·{" "}
                  {r.excuseReviewReason === "tier_changed"
                    ? "the finding's tier has changed since then"
                    : (r.excuseBreaches || []).map((b) =>
                        `${b.category} is now ${formatMoney(b.current)} (was ${formatMoney(b.previous)} at review) — over your ${formatMoney(b.threshold)} threshold`
                      ).join("; ")}
                </p>
              )}
            </div>
          );
        })}
      </div>
      {hiddenProviders > 0 && !showLoadMore && (
        <p className="text-white/25 text-xs mt-2">Showing {shownProviderCount.toLocaleString()} of {count.toLocaleString()} providers by highest amount · export full data for the rest</p>
      )}
      {showLoadMore && (
        <div className="mt-2 flex items-center gap-2">
          <button
            onClick={() => onLoadMore(tier)}
            disabled={pagination?.loading}
            className="text-xs px-3 py-1.5 rounded-lg border border-white/15 text-white/50 hover:text-white/80 hover:border-white/30 transition-all disabled:opacity-50"
          >
            {pagination?.loading ? "Loading…" : isExpanded ? `Load more (${shown.length.toLocaleString()} of ${(findingCount ?? count).toLocaleString()} shown)` : "Load More"}
          </button>
        </div>
      )}
    </div>
  );
}

function Section({ title, subtitle, rows, tierCounts, tierFindingCounts, icon, colorClass, onOpenExcuse, onReverse, reversingKey, paginationEnabled, tierPagination, onLoadMore, applyOverrides }) {
  const byTier = { 1: [], 2: [], 3: [], 4: [], 5: [] };
  rows.forEach((r) => { if (byTier[r.tier]) byTier[r.tier].push(r); });
  // Once a tier has been expanded via Load More, its live-paginated rows
  // (with excuse overrides re-applied, same as everywhere else) replace
  // the original top-5 preview for that tier entirely.
  if (paginationEnabled && tierPagination) {
    [1, 2, 3].forEach((t) => {
      if (tierPagination[t]?.expanded) {
        byTier[t] = applyOverrides(tierPagination[t].rows);
      }
    });
  }
  // colorClass presence controls whether this gets the bold, colored header
  // bar (used to distinguish two scopes on a vendor scan) or the plain
  // understated label (used for a single-scope provider scan, where there's
  // nothing to visually distinguish it FROM).
  return (
    <div className="mb-8">
      {colorClass ? (
        <div className={`flex items-start gap-2 px-3 py-2.5 rounded-lg border mb-2 ${colorClass.bg} ${colorClass.border}`}>
          <span className="text-base shrink-0 leading-none mt-0.5">{icon}</span>
          <div>
            <p className={`text-sm font-semibold ${colorClass.text}`}>{title}</p>
            {subtitle && <p className="text-white/40 text-xs mt-0.5">{subtitle}</p>}
          </div>
        </div>
      ) : (
        <>
          <p className="text-xs font-medium text-white/70 mb-0.5">{title}</p>
          {subtitle && <p className="text-white/30 text-xs mb-3">{subtitle}</p>}
        </>
      )}
      <div className={`border-l-2 ${colorClass ? colorClass.border : "border-white/15"} pl-3`}>
        {[1, 2, 3, 4, 5].map((t) => (
          <TierGroup
            key={t}
            tier={t}
            rows={byTier[t]}
            count={tierCounts ? (tierCounts[t] ?? 0) : byTier[t].length}
            findingCount={tierFindingCounts ? (tierFindingCounts[t] ?? 0) : null}
            onOpenExcuse={onOpenExcuse}
            onReverse={onReverse}
            reversingKey={reversingKey}
            paginationEnabled={paginationEnabled}
            pagination={tierPagination?.[t]}
            onLoadMore={onLoadMore}
          />
        ))}
      </div>
    </div>
  );
}

function exportXlsx(report, fullRows) {
  const { result, report_type, rubric_version, created_at, id, year } = report;
  const isVendorScan = report_type === "vendor_scan";
  const scopeLabel = isVendorScan
    ? `Vendor screening — ${result.trackedProviderCount.toLocaleString()} tracked provider(s), ${result.marketWideProviderCount.toLocaleString()} connected market-wide`
    : `${result.providerCount.toLocaleString()} provider(s) screened`;

  const notesRows = [
    ["ProviderPayWatch — conflict screening methodology"],
    ["Rubric version", rubric_version],
    [],
    ["Tier", "Trigger"],
    [1, "Active federal exclusion (OIG or SAM) — automatic, overrides everything else"],
    [2, "A financial relationship with a confirmed vendor or a member of its corporate family (known-vendor overlap), or triple interest (with at least one concurrent overlap)"],
    [3, "POD tier 1 flag, a payment audit threshold finding, a COI disclosure gap or disclosed vendor relationship, dual interest (with at least one concurrent overlap), or a triple interest where none of the overlaps were concurrent"],
    [4, "POD tier 2 flag, a blind-spot relationship, a dual interest where none of the overlaps were concurrent, or any payment/ownership/research relationship that doesn't trigger a higher-severity signal above"],
    [5, "No signals found"],
    [],
    ["What does Tier 4's 'no other flags' mean?", "Tier 4 includes every ordinary provider-company financial relationship on file — a payment, ownership stake, or research grant — that isn't already dual/triple interest, POD-flagged, or a known-vendor overlap. This is what a Blind-Spot finding usually is: a real, documented relationship between a provider and a company that hasn't triggered anything more specific and isn't with a confirmed vendor or a member of a confirmed vendor's corporate family. It's the largest tier by volume for exactly this reason — most individual relationships are routine."],
    [],
    ["Signal type", "Definition"],
    ["Exclusion", "Provider is on the OIG LEIE or SAM.gov active exclusion list"],
    ["Triple interest", "Payments, ownership, and research funding all present with the same company"],
    ["Dual interest", "Any two of payments, ownership, or research present with the same company"],
    ["Known-vendor overlap", "Provider has a financial relationship with a company that is a confirmed institutional vendor, or that belongs to the same corporate family as one. A family match is stated on the finding ('via family: <vendor>'); the finding stays on the entity CMS reports, with its own amounts."],
    ["Blind-spot", "Provider has a financial relationship with a company that is neither a confirmed vendor nor in the corporate family of one"],
    ["POD tier 1 / tier 2", "Company is flagged as a potential Physician Owned Distributorship per OIG Work Plan risk indicators"],
    ["Payment audit threshold", "The provider's combined payments in the latest program year, across all companies, exceed an audit threshold set for your account (general payments, ownership value, direct research, or PI research). Not tied to any single company."],
    ["Disclosure gap", "CMS shows a payment, ownership, or research relationship between the provider and a company at or above your disclosure thresholds in at least one program year of your configured window, but the provider's COI questionnaire does not disclose that company. Only providers with a complete, fully matched questionnaire are evaluated, and companies are compared by corporate family. A family member's disclosed relationship with the company appears on the finding but does not count as the provider disclosing their own payments."],
    ["Disclosed vendor relationship", "The provider's COI questionnaire discloses a relationship (their own or an immediate family member's) with a confirmed vendor, and CMS shows no relationship between the provider and that company in any year. If CMS does show one, it appears as a known-vendor overlap noted as disclosed."],
    ["COI disclosure checks", "Providers with no questionnaire on file, or with disclosure matches still unresolved, are not evaluated for disclosure gaps. The report header counts them, so no finding for those providers is not a clean result."],
    ["Market-Wide Exclusion", "(Vendor screens only) A provider connected to this vendor is independently on the exclusion list"],
    ["Market-wide dual/triple interest", "(Vendor screens only) Count of providers anywhere — not just tracked ones — with this pattern involving the vendor"],
    ["Vendor POD", "(Vendor screens only) The vendor itself carries a POD flag, independent of any specific provider"],
    [],
    ["Same period vs. different periods", "Dual/triple interest findings note whether the overlapping relationships occurred in the same reporting year (same period) or at different, non-overlapping times (different periods). A finding with no concurrent overlap at all is shown at a lower tier than one with at least one — see the tier table above."],
    [],
    ["Data sources", `CMS Open Payments${year ? `, program year ${year}` : ", 2016–2025"} (payments, ownership, research) · OIG LEIE and SAM.gov exclusion data, refreshed daily · NPPES (provider name, specialty, state) · Your My Providers list (which providers were screened) · Your My Vendors list (which companies count as a confirmed vendor, together with their corporate families) · Your COI questionnaire uploads (provider-submitted disclosures, where on file)`],
    ["Disclaimer", "This report supports compliance risk review and does not constitute a legal determination. Consult qualified legal or compliance counsel for final determinations."],
  ];

  const summaryRows = [
    ["ProviderPayWatch — conflict screening report"],
    ["Report ID", id],
    ["Report type", isVendorScan ? "Vendor conflict screening" : "Provider conflict screening"],
    ["Scope", scopeLabel],
    ["Generated", new Date(created_at).toLocaleString()],
    ["Rubric version", rubric_version],
    [],
    ["Tier", "Label", "Count"],
    ...[1, 2, 3, 4, 5].map((t) => [t, TIER_META[t].label, result.tierCounts?.[t] ?? 0]),
  ];

  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(notesRows), "Notes");
  XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(summaryRows), "Summary");
  for (let t = 1; t <= 5; t++) {
    const tierRows = sortRowsForExport(
      (fullRows || []).filter((r) => r.tier === t && r.signalType !== "Market-Wide Exclusion Rate")
    );
    const headers = isVendorScan
      ? ["Scope", "Signal type", "Provider", "NPI", "Company ID", "Finding", "Amount"]
      : ["Signal type", "Provider", "NPI", "Company ID", "Finding", "Amount"];
    const sheetRows = [
      headers,
      ...tierRows.map((r) => {
        const row = [r.signalType, r.providerName || "", r.npi || "", r.companyId || "", r.finding, r.amount || ""];
        if (isVendorScan) {
          const scopeLabel = r.scope === "tracked" ? "My Tracked Providers" : r.scope === "market" ? "Market-Wide Providers" : "";
          row.unshift(scopeLabel);
        }
        return row;
      }),
    ];
    const ws = XLSX.utils.aoa_to_sheet(sheetRows);
    applyCurrencyColumn(ws, isVendorScan ? 6 : 5, tierRows.length);
    XLSX.utils.book_append_sheet(wb, ws, `Tier ${t}`);
  }
  const filePrefix = isVendorScan ? "PPW Vendor Conflict Report" : "PPW Provider Conflict Report";
  XLSX.writeFile(wb, `${filePrefix}_${id}.xlsx`);
}

export default function ConflictReportView({ report, userId }) {
  const [exporting, setExporting] = useState(false);
  const [excusePopupFor, setExcusePopupFor] = useState(null);
  const [excuseOverrides, setExcuseOverrides] = useState({});
  const [reversingKey, setReversingKey] = useState(null);
  // Per-tier pagination state for the Tracked Providers / Findings
  // section only — Market-Wide Signals never paginates, since nothing
  // there is excusable and top-5-plus-aggregate is sufficient as-is.
  const [tierPagination, setTierPagination] = useState({});

  function excuseRowKey(r) {
    return `${r.npi}::${r.companyId}::${r.signalType}`;
  }

  // Mirrors checkExcuseStatus in api/_lib/excuseLogic.js — duplicated
  // here since that's a server-only Node module. Needed for immediate
  // feedback right after creating/updating an excuse, before the next
  // report generation would otherwise refresh this.
  const CHANGE_EPSILON = 0.01;

  function computeExcuseStatus(row, excuse) {
    if (row.tier !== excuse.excused_tier) {
      return { status: "needs_review", reason: "tier_changed" };
    }
    if (excuse.excuse_regardless_of_amount) {
      return { status: "holding" };
    }
    const checks = row.signalType === "Payment Audit Threshold"
      ? [
          ["General payments", row.paymentAmount, "excused_general_amount", excuse.threshold_general_amount],
          ["Ownership", row.ownershipAmount, "excused_ownership_amount", excuse.threshold_ownership_amount],
          ["Direct research", row.directResearchAmount, "excused_direct_research_amount", excuse.threshold_direct_research_amount],
          ["PI research", row.piResearchAmount, "excused_pi_research_amount", excuse.threshold_pi_research_amount],
        ]
      : [
          ["General payments", row.paymentAmount, "excused_general_amount", excuse.threshold_general_amount],
          ["Ownership", row.ownershipAmount, "excused_ownership_amount", excuse.threshold_ownership_amount],
          ["Research", row.researchAmount, "excused_research_amount", excuse.threshold_research_amount],
        ];
    const breaches = [];
    checks.forEach(([category, current, snapshotKey, threshold]) => {
      const currentVal = current || 0;
      const snapshotVal = excuse[snapshotKey] || 0;
      const hasChanged = Math.abs(currentVal - snapshotVal) > CHANGE_EPSILON;
      if (hasChanged && currentVal >= (threshold ?? 0)) {
        breaches.push({ category, current: currentVal, previous: snapshotVal, threshold: threshold ?? 0 });
      }
    });
    if (breaches.length > 0) return { status: "needs_review", reason: "threshold_breached", breaches };
    return { status: "holding" };
  }

  function applyExcuseOverrides(rows) {
    return rows.map((r) => {
      const override = excuseOverrides[excuseRowKey(r)];
      if (!override) return r;
      if (override.status === "reversed") {
        const { excuseStatus, excusedBy, excusedAt, excuseNotes, excuseThresholdGeneral, excuseThresholdOwnership, excuseThresholdResearch, excuseRegardlessOfAmount, excuseId, ...rest } = r;
        return rest;
      }
      const statusResult = computeExcuseStatus(r, override);
      return {
        ...r,
        excuseId: override.id,
        excuseStatus: statusResult.status,
        excuseReviewReason: statusResult.reason || null,
        excuseBreaches: statusResult.breaches || null,
        excusedBy: override.reviewed_by_user_id,
        excusedAt: override.reviewed_at,
        excuseNotes: override.review_notes,
        excuseThresholdGeneral: override.threshold_general_amount,
        excuseThresholdOwnership: override.threshold_ownership_amount,
        excuseThresholdResearch: override.threshold_research_amount,
        excuseThresholdDirectResearch: override.threshold_direct_research_amount,
        excuseThresholdPiResearch: override.threshold_pi_research_amount,
        excuseRegardlessOfAmount: override.excuse_regardless_of_amount,
      };
    });
  }

  function handleExcuseSuccess(excuse) {
    setExcuseOverrides((prev) => ({ ...prev, [`${excuse.npi}::${excuse.company_id}::${excuse.signal_type}`]: excuse }));
    setExcusePopupFor(null);
  }

  // First click on a tier switches it into full pagination from offset 0
  // rather than trying to continue past the top-5 preview — the preview's
  // selection groups by provider (so a provider with several company
  // relationships can occupy more than one of the "top 5" slots), which
  // doesn't line up cleanly with a flat, amount-sorted offset. Simpler and
  // safer to just re-fetch the tier properly the first time it's expanded.
  async function loadMoreForTier(tier) {
    const current = tierPagination[tier] || { rows: [], offset: 0, hasMore: true, loading: false, expanded: false };
    if (current.loading) return;
    setTierPagination((prev) => ({ ...prev, [tier]: { ...current, loading: true, expanded: true } }));
    try {
      const res = await apiFetch("/api/get-tier-findings", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ userId, reportId: report.id, tier, offset: current.offset, limit: 25 }),
      });
      const data = await res.json();
      setTierPagination((prev) => {
        const prevState = prev[tier] || current;
        return {
          ...prev,
          [tier]: {
            rows: [...prevState.rows, ...(data.rows || [])],
            offset: prevState.offset + (data.rows?.length || 0),
            hasMore: !!data.hasMore,
            loading: false,
            expanded: true,
          },
        };
      });
    } catch {
      setTierPagination((prev) => ({ ...prev, [tier]: { ...current, loading: false } }));
    }
  }

  // Runs on every report load, not just right after an action taken in
  // this same browser session — checks for excuses that may have been
  // created or reversed since this report was generated (by anyone on
  // the team, or by this same user in an earlier session), since the
  // report's own stored result only reflects status as of generation
  // time and a page refresh would otherwise silently revert to that.
  useEffect(() => {
    if (!report?.result?.rows) return;
    const paginatedRows = Object.values(tierPagination).flatMap((p) => p.rows || []);
    const eligible = [...report.result.rows, ...paginatedRows].filter(isRowExcusable);
    if (eligible.length === 0) return;
    const npis = [...new Set(eligible.map((r) => r.npi))];
    const companyIds = [...new Set(eligible.map((r) => r.companyId))];

    apiFetch("/api/get-excuse-status", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ userId, npis, companyIds }),
    })
      .then((res) => res.json())
      .then((data) => {
        if (!data.excuses || data.excuses.length === 0) return;
        setExcuseOverrides((prev) => {
          const next = { ...prev };
          data.excuses.forEach((excuse) => {
            const key = `${excuse.npi}::${excuse.company_id}::${excuse.signal_type}`;
            // Don't clobber an override already set in this same session
            // (e.g. right after Undo) with a fetch that may have started
            // before that action completed.
            if (!(key in next)) next[key] = excuse;
          });
          return next;
        });
      })
      .catch(() => {
        // Non-fatal — the report still displays its generation-time
        // excuse status even if this live check fails.
      });
  }, [report?.id, Object.keys(tierPagination).length]);

  async function handleReverse(row) {
    const key = excuseRowKey(row);
    if (reversingKey) return;
    const excuseId = row.excuseId || excuseOverrides[key]?.id;
    if (!excuseId) return;
    setReversingKey(key);
    try {
      const res = await apiFetch("/api/manage-excuse", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ userId, action: "reverse", excuseId }),
      });
      if (res.ok) {
        setExcuseOverrides((prev) => ({ ...prev, [key]: { status: "reversed" } }));
      }
    } catch {
      // leave the badge as-is; the user can retry
    } finally {
      setReversingKey(null);
    }
  }

  async function handleExportClick() {
    if (exporting) return;
    setExporting(true);
    try {
      const res = await apiFetch(`/api/conflict-report-findings?userId=${userId}&reportId=${report.id}`);
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Export failed");
      exportXlsx(report, data.rows);
    } catch (err) {
      alert(err.message || "Export failed. Please try again.");
    } finally {
      setExporting(false);
    }
  }
  if (!report || report.status !== "complete" || !report.result) {
    return <p className="text-white/40 text-sm">Report not ready.</p>;
  }
  const { result, report_type } = report;
  const isVendorScan = report_type === "vendor_scan";
  const trackedRowsRaw = isVendorScan ? (result.rows || []).filter((r) => r.scope === "tracked") : (result.rows || []);
  const marketRows = applyExcuseOverrides(isVendorScan ? (result.rows || []).filter((r) => r.scope === "market") : []);
  const trackedRows = applyExcuseOverrides(trackedRowsRaw);

  return (
    <div className="max-w-3xl mx-auto px-4 py-6">
      <style>{`
        @media print {
          @page { margin: 0.75in; size: letter; }
          body { background: white !important; color: #111 !important; -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
          nav, header, footer, .print-hide { display: none !important; }
          main { background: white !important; max-width: 100% !important; padding: 0 !important; }
          .text-white\\/80, .text-white\\/70, .text-white\\/60 { color: #374151 !important; }
          .text-white\\/40, .text-white\\/30, .text-white\\/25 { color: #6b7280 !important; }
          .text-red-400 { color: #dc2626 !important; }
          .text-orange-400 { color: #ea580c !important; }
          .text-amber-400 { color: #b45309 !important; }
          .text-blue-400 { color: #2563eb !important; }
          .bg-red-500\\/10 { background: #fef2f2 !important; border-color: #fca5a5 !important; }
          .bg-orange-500\\/10 { background: #fff7ed !important; border-color: #fdba74 !important; }
          .bg-amber-500\\/10 { background: #fffbeb !important; border-color: #fcd34d !important; }
          .bg-blue-500\\/10 { background: #eff6ff !important; border-color: #93c5fd !important; }
          .text-gold-400 { color: #b45309 !important; }
          .bg-gold-500\\/10 { background: #fffbeb !important; border-color: #fcd34d !important; }
          .text-teal-400 { color: #0f766e !important; }
          .bg-teal-500\\/10 { background: #f0fdfa !important; border-color: #5eead4 !important; }
        }
      `}</style>

      <div className="print-hide flex items-center justify-between mb-1">
        <h1 className="font-display text-2xl text-white">
          {isVendorScan ? `Vendor Conflict Screening${result.companyName ? ` — ${result.companyName}` : ""}` : "Provider Conflict Screening"}
        </h1>
        <div className="flex gap-2">
          <button onClick={() => window.print()} className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg border border-white/20 text-white/60 hover:text-white hover:border-white/40 bg-white/5 transition-all">
            ⎙ Print / Save PDF
          </button>
          <button onClick={handleExportClick} disabled={exporting} className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg border border-white/20 text-white/60 hover:text-white hover:border-white/40 bg-white/5 transition-all disabled:opacity-50">
            {exporting ? "⏳ Preparing…" : "⬇ Export full data (.xlsx)"}
          </button>
        </div>
      </div>

      <div className="border border-white/10 rounded-xl px-4 py-3 mb-6 bg-white/[0.02]">
        <div className="grid grid-cols-2 gap-x-6 gap-y-1 text-xs">
          {isVendorScan && result.companyName && (
            <div className="col-span-2"><span className="text-white/30">Vendor:</span> <span className="text-white/80 font-medium">{result.companyName}</span></div>
          )}
          <div><span className="text-white/30">Report ID:</span> <span className="text-white/60 font-mono">{report.id}</span></div>
          <div><span className="text-white/30">Generated:</span> <span className="text-white/60">{new Date(report.created_at).toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" })}</span></div>
          <div><span className="text-white/30">Scope:</span> <span className="text-white/60">
            {isVendorScan ? `${result.trackedProviderCount.toLocaleString()} tracked provider(s), ${result.marketWideProviderCount.toLocaleString()} connected market-wide` : `${result.providerCount.toLocaleString()} provider(s) screened`}
          </span></div>
          <div><span className="text-white/30">Rubric version:</span> <span className="text-white/60">{report.rubric_version}</span></div>
          <div className="col-span-2"><span className="text-white/30">Source:</span> <span className="text-white/60">CMS Open Payments{report.year ? `, program year ${report.year}` : ", 2016–2025"} (payments, ownership, research) · OIG LEIE and SAM.gov exclusions, refreshed daily</span></div>
        </div>
        {!isVendorScan && result.vendorFamilyMatchError && (
          <p className="mt-3 pt-3 border-t border-white/5 text-xs text-amber-400/80">
            Vendor family matching could not run for this report ({result.vendorFamilyMatchError}). Known-Vendor Overlap used exact company matches only, so some overlaps may appear as Blind-Spot.
          </p>
        )}
        {!isVendorScan && result.disclosureCoverage && (
          <div className="mt-3 pt-3 border-t border-white/5 space-y-1 text-xs">
            {result.disclosureCoverage.error ? (
              <p className="text-amber-400/80">
                COI disclosure checks could not run for this report ({result.disclosureCoverage.error}). The absence of Disclosure Gap findings below does not mean there are none.
              </p>
            ) : (
              <>
                <p className="text-white/40">
                  COI disclosure checks: {result.disclosureCoverage.evaluated.toLocaleString()} of {result.providerCount.toLocaleString()} providers evaluated,{" "}
                  {result.disclosureCoverage.pendingMatching.toLocaleString()} pending company matching,{" "}
                  {result.disclosureCoverage.noQuestionnaire.toLocaleString()} with no questionnaire on file.
                </p>
                {result.disclosureCoverage.unresolvedIdentities > 0 && (
                  <p className="text-white/30">
                    {result.disclosureCoverage.unresolvedIdentities.toLocaleString()} questionnaire entries are not yet matched to a provider and cannot be checked.
                  </p>
                )}
                {result.disclosureCoverage.thresholdsConfigured ? (
                  <>
                    <p className="text-white/25">
                      Disclosure Gap checks program years {result.disclosureCoverage.window.startYear === result.disclosureCoverage.window.endYear
                        ? result.disclosureCoverage.window.startYear
                        : `${result.disclosureCoverage.window.startYear}–${result.disclosureCoverage.window.endYear}`}, using your Disclosure Thresholds.
                    </p>
                    <DisclosureChecksNote thresholds={result.disclosureCoverage.thresholds} />
                  </>
                ) : (
                  <p className="text-amber-400/80">
                    Disclosure Gap checks are off: no Disclosure Thresholds are set. Set them on the COI page.
                  </p>
                )}
              </>
            )}
          </div>
        )}
        {isVendorScan && (
          <p className="text-white/25 text-xs mt-2">
            "Connected market-wide" counts every distinct provider with a documented payment, ownership, or research relationship to this specific vendor entity, across all years on file. Large manufacturers often report under multiple separate CMS entities — this count reflects only the entity named above, not its parent company as a whole.
          </p>
        )}
        {isVendorScan && result.hasNoOwnershipData && (
          <p className="text-white/25 text-xs mt-2">
            This vendor has no recorded physician ownership relationships in CMS data. Triple Interest, and any Dual Interest type involving ownership, will always read zero for this vendor as a structural result — not necessarily an indication of lower overall risk.
          </p>
        )}
        <p className="text-white/25 text-xs mt-3 pt-3 border-t border-white/5">This report supports compliance risk review and does not constitute a legal determination. Consult qualified legal or compliance counsel for final determinations.</p>
      </div>

      {isVendorScan ? (
        <>
          <Section
            title="Your Tracked Providers"
            subtitle="Providers from your My Providers list(s), screened specifically against this vendor"
            rows={trackedRows}
            tierCounts={result.tierCounts}
            tierFindingCounts={result.tierFindingCounts}
            icon="👤"
            colorClass={{ bg: "bg-gold-500/10", border: "border-gold-500/30", text: "text-gold-400" }}
            onOpenExcuse={setExcusePopupFor}
            onReverse={handleReverse}
            reversingKey={reversingKey}
            paginationEnabled={true}
            tierPagination={tierPagination}
            onLoadMore={loadMoreForTier}
            applyOverrides={applyExcuseOverrides}
          />
          <Section
            title="Market-Wide Signals"
            subtitle="Every provider connected to this vendor, not just yours"
            rows={marketRows}
            tierCounts={result.marketTierCounts}
            tierFindingCounts={result.marketTierFindingCounts}
            icon="🌐"
            colorClass={{ bg: "bg-teal-500/10", border: "border-teal-500/30", text: "text-teal-400" }}
            onOpenExcuse={setExcusePopupFor}
            onReverse={handleReverse}
            reversingKey={reversingKey}
          />
        </>
      ) : (
        <Section
          title="Findings"
          rows={trackedRows}
          tierCounts={result.tierCounts}
          tierFindingCounts={result.tierFindingCounts}
          onOpenExcuse={setExcusePopupFor}
          onReverse={handleReverse}
          reversingKey={reversingKey}
          paginationEnabled={true}
          tierPagination={tierPagination}
          onLoadMore={loadMoreForTier}
          applyOverrides={applyExcuseOverrides}
        />
      )}

      {excusePopupFor && (
        <ExcusePopup
          finding={excusePopupFor}
          reportId={report.id}
          userId={userId}
          fetchFn={apiFetch}
          existingExcuse={excusePopupFor.excuseId ? {
            threshold_general_amount: excusePopupFor.excuseThresholdGeneral,
            threshold_ownership_amount: excusePopupFor.excuseThresholdOwnership,
            threshold_research_amount: excusePopupFor.excuseThresholdResearch,
            excuse_regardless_of_amount: excusePopupFor.excuseRegardlessOfAmount,
            review_notes: excusePopupFor.excuseNotes,
          } : null}
          onClose={() => setExcusePopupFor(null)}
          onSuccess={handleExcuseSuccess}
        />
      )}
    </div>
  );
}